import { ReactElement, useCallback } from 'react';

import { MagistralDrugRawMaterial } from '@dreryk/drerykweb-medicine-api';

import { assertUnreachable, FormContextValues } from '@dreryk/react-common';
import { Form, FormContent, Scroll } from '@dreryk/react-components';

import { MagistralDrugTemplateUse } from '../../prescriptionTemplates/magistralDrug/MagistralDrugTemplates.types';
import { MagistralDrugTemplateModalOpener } from '../../prescriptionTemplates/magistralDrug/modal/MagistralDrugTemplateModal.opener';
import { MagistralDrugTemplateInsert } from '../../prescriptionTemplates/magistralDrug/readOnly/MagistralDrugReadOnly.types';
import { AdditionalDetailsTextInput } from '../../shared/formFields/magistralDrug/AdditionalDetailsTextInput';
import { DrugFormTextInput } from '../../shared/formFields/magistralDrug/DrugFormTextInput';
import { MedicineRawMaterialTable } from '../../shared/formFields/magistralDrug/medicineRawMaterials/MedicineRawMaterialTable';

import { DrugNameTextInput } from './fields/DrugNameTextInput';
import { useMagistralDrugCreateFormController } from './MagistralDrugCreateForm.hooks';
import { MagistralDrugCreateFormBean } from './MagistralDrugCreateForm.types';
import { mapMagistralDrugRawMaterialToMedicineRawMaterialTableBean } from './MagistralDrugCreateForm.utils';

export type MagistralDrugCreateFormChildrenRenderProps = {
   handleSubmit: FormContextValues<MagistralDrugCreateFormBean>['handleSubmit'];
   reset: FormContextValues<MagistralDrugCreateFormBean>['reset'];
   formState: FormContextValues<MagistralDrugCreateFormBean>['formState'];
   getValues: FormContextValues<MagistralDrugCreateFormBean>['getValues'];
   onInsert: MagistralDrugTemplateInsert;
   onUse: MagistralDrugTemplateUse;
};

type MagistralDrugCreateFormProps = {
   readonly children: (renderProps: MagistralDrugCreateFormChildrenRenderProps) => ReactElement;
};

// eslint-disable-next-line max-lines-per-function
export const MagistralDrugCreateForm = (props: MagistralDrugCreateFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      reset,
      getValues,
      getValue,
      setValue,
      formState,
      markDirty,
   } = useMagistralDrugCreateFormController();

   const handleStringValueAppend = useCallback(
      (fieldName: 'drugName' | 'drugForm' | 'additionalDetails', value: string | undefined): void => {
         const currentFieldValue = getValue(fieldName);
         setValue(fieldName, `${currentFieldValue ? `${currentFieldValue} ` : ''}${value ?? ''}`, {
            shouldDirty: true,
         });
      },
      [getValue, setValue]
   );

   const handleInsertFromTemplate: MagistralDrugTemplateInsert = useCallback(
      (type, value): void => {
         switch (type) {
            case 'templateName':
               return handleStringValueAppend('drugName', value as string);
            case 'drugForm':
               return handleStringValueAppend('drugForm', value as string);
            case 'additionalInformation':
               return handleStringValueAppend('additionalDetails', value as string);
            case 'ingredient':
               return setValue(
                  'medicineRawMaterials',
                  [
                     ...getValue('medicineRawMaterials'),
                     mapMagistralDrugRawMaterialToMedicineRawMaterialTableBean(value as MagistralDrugRawMaterial),
                  ],
                  { shouldDirty: true }
               );
            default:
               return assertUnreachable(type);
         }
      },
      [handleStringValueAppend, setValue, getValue]
   );

   const handleTemplateUse: MagistralDrugTemplateUse = (template) => {
      reset({
         drugName: template.name,
         drugForm: template.form,
         additionalDetails: template.additionalDetails,
         medicineRawMaterials: template.magistralDrugMedicineRawMaterials.map(
            mapMagistralDrugRawMaterialToMedicineRawMaterialTableBean
         ),
      });
      markDirty();
   };

   return (
      <Form noValidate>
         <Scroll testId={'magistralDrug'}>
            <FormContent testId={'content'}>
               <MagistralDrugTemplateModalOpener
                  defaultTemplate={undefined}
                  onInsert={handleInsertFromTemplate}
                  shouldConfirmUse={formState.isDirty}
                  onUse={handleTemplateUse}
               >
                  {({ openModal }) => <DrugNameTextInput register={register} onInsertClick={openModal} />}
               </MagistralDrugTemplateModalOpener>
               <MedicineRawMaterialTable register={register} />
               <DrugFormTextInput register={register} />
               <AdditionalDetailsTextInput register={register} />
            </FormContent>
         </Scroll>
         {props.children({
            handleSubmit: handleSubmit,
            reset: reset,
            formState: formState,
            getValues: getValues,
            onInsert: handleInsertFromTemplate,
            onUse: handleTemplateUse,
         })}
      </Form>
   );
};
